import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import MuiLink from "@mui/material/Link";
import {ColumnDef} from "@tanstack/react-table";
import {DataTypeOperator} from "crqlar-react-common/dist/models/audience/filter.model";
import type {AggregationFolioListItem} from "crqlar-react-common/dist/models/aggregation/aggregation.model";
import type {FolioListRequest} from "crqlar-react-common/dist/models/billing/folioList.request";
import {FolioAggregationService} from "crqlar-react-common/dist/services/aggregation/folioAggregation.service";
import {FolioService} from "crqlar-react-common/dist/services/billing/folio.service";
import {getAmountFormatted} from "crqlar-react-common/dist/utils/currency.util";
import {formatFullDateTime, formatFullDMY} from "crqlar-react-common/dist/utils/date.util";
import {useFolioFilterOptions} from "crqlar-react-common/dist/utils/hooks/folio/useFolioData";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {Link as RouterLink, useNavigate} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableExport,
    ServerTableFilter,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTablePrint,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    serverTableExportLimit,
    type ServerTableExportQuery,
    type ServerTableUrlStateOptions,
    useServerTable
} from "../../../common/serverTable";
import {FolioStatusElement} from "../../../common/uiElements/FolioStatusElement";
import SettingsListPage from "../../layout/SettingsListPage";

interface Props {
    profileId?: string
    reservationId?: string
}

const FOLIOS_URL_STATE: ServerTableUrlStateOptions = {
    filterAliases: [{
        queryParam: 'profileId',
        field: 'profileId',
        operator: DataTypeOperator.IN
    }]
};

const formatReservation = (reservation?: AggregationFolioListItem['reservation']) => {
    if (!reservation) return '-';

    const dates = [reservation.startDate, reservation.endDate]
        .map(date => (date ? formatFullDMY(date) : ''))
        .filter(Boolean)
        .join(' - ');

    return [reservation.serviceName, dates].filter(Boolean).join(' | ') || reservation.id;
};

const FoliosList = (props: Props) => {
    useTrackScreenVisit('system folios');

    const {t} = useTranslation();
    const navigate = useNavigate();
    const filterOptions = useFolioFilterOptions().data || [];
    const embedded = !!(props.profileId || props.reservationId);

    const columns = useMemo<ColumnDef<AggregationFolioListItem>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: folio => folio.name,
            cell: ({row}) => <Box>{row.original.name ?? ''}</Box>,
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true
            }
        },
        {
            id: 'profile',
            header: t('Profile'),
            accessorFn: folio => folio.profileName,
            cell: ({row}) => <Box>{row.original.profileName || t('Unknown')}</Box>,
            size: 220,
            meta: {
                defaultActive: true,
                grow: true,
                printable: true,
                printDefault: true,
                printCell: row => row.original.profileName || t('Unknown')
            }
        },
        {
            id: 'reservation',
            header: t('Reservation'),
            accessorFn: folio => folio.reservation?.serviceName,
            cell: ({row}) => {
                const reservation = row.original.reservation;
                if (!reservation) return <Box>-</Box>;
                const label = formatReservation(reservation);
                const trailingTokenStart = label.lastIndexOf(' ') + 1;

                return (
                    <MuiLink
                        component={RouterLink}
                        to={`/system/reservations/${reservation.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        underline="hover"
                        onClick={event => event.stopPropagation()}
                        sx={{lineHeight: 1.5}}
                    >
                        {label.slice(0, trailingTokenStart)}
                        <Box
                            component="span"
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                whiteSpace: 'nowrap',
                                textDecoration: 'inherit'
                            }}
                        >
                            {label.slice(trailingTokenStart)}
                            <OpenInNewIcon
                                color="primary"
                                sx={{fontSize: 16, ml: 0.5, flex: '0 0 auto'}}
                            />
                        </Box>
                    </MuiLink>
                );
            },
            size: 360,
            meta: {
                defaultActive: true,
                printable: true,
                printDefault: true,
                printCell: row => formatReservation(row.original.reservation)
            }
        },
        {
            id: 'total',
            header: t('Total'),
            accessorFn: folio => folio.total?.amountCents,
            cell: ({row}) => <Box>{getAmountFormatted(row.original.total, '-')}</Box>,
            size: 140,
            meta: {
                defaultActive: true,
                align: 'right',
                isServerSortable: true,
                serverSortField: 'total.amountCents',
                printable: true,
                printDefault: true,
                printCell: row => getAmountFormatted(row.original.total, '-')
            }
        },
        {
            id: 'provider',
            header: t('Provider'),
            accessorFn: folio => folio.provider,
            cell: ({row}) => <Box>{row.original.provider || t('Unknown')}</Box>,
            size: 140,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => row.original.provider || t('Unknown')
            }
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: folio => folio.status,
            cell: ({row}) => <FolioStatusElement status={row.original.status}/>,
            size: 160,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => <FolioStatusElement status={row.original.status} asPrint/>
            }
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: folio => folio.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 260,
            meta: {defaultActive: false, isServerSortable: true, printable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: folio => folio.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {
                defaultActive: true,
                isServerSortable: true,
                printable: true,
                printDefault: true,
                printCell: row => formatFullDateTime(row.original.updateDate)
            }
        }
    ], [t]);

    const controller = useServerTable({
        tableId: 'system-folios',
        columns,
        getRowId: folio => folio.id,
        onRowClick: folio => navigate(`/system/folios/${folio.id}`),
        // newest created first — Mongo ObjectIds sort by creation time (matches the old CList default)
        initialSorting: [{id: 'id', desc: true}],
        urlState: embedded ? false : FOLIOS_URL_STATE,
        queryDependencies: [props.profileId, props.reservationId],
        queryFn: ({pagination, searchQuery, filterRules, sorting}) => {
            const request: FolioListRequest = {
                profileIds: props.profileId ? [props.profileId] : undefined,
                reservationIds: props.reservationId ? [props.reservationId] : undefined,
                query: searchQuery || undefined,
                limit: {
                    offset: pagination.pageIndex * pagination.pageSize,
                    limit: pagination.pageSize,
                    sort: sorting ? [sorting] : undefined
                }
            };
            const profileRule = filterRules.find(rule => rule.field === 'profileId');
            const profileFilterIds = (profileRule?.textValues?.filter(Boolean) || []) as string[];
            if (profileFilterIds.length) {
                request.profileIds = props.profileId
                    ? (profileFilterIds.includes(props.profileId) ? [props.profileId] : [])
                    : profileFilterIds;
            }
            return FolioAggregationService.listFolios(request);
        }
    });

    // The export's request: the list's own, without paging.
    const exportRequest = useCallback((
        {searchQuery, filterRules, sorting}: ServerTableExportQuery
    ): FolioListRequest => {
        const request: FolioListRequest = {
            profileIds: props.profileId ? [props.profileId] : undefined,
            reservationIds: props.reservationId ? [props.reservationId] : undefined,
            query: searchQuery || undefined,
            limit: serverTableExportLimit(sorting)
        };
        const profileRule = filterRules.find(rule => rule.field === 'profileId');
        const profileFilterIds = (profileRule?.textValues?.filter(Boolean) || []) as string[];
        if (profileFilterIds.length) {
            request.profileIds = props.profileId
                ? (profileFilterIds.includes(props.profileId) ? [props.profileId] : [])
                : profileFilterIds;
        }
        return request;
    }, [props.profileId, props.reservationId]);

    const handleCreate = async () => {
        let profileIdToUse = props.profileId;
        if (!profileIdToUse) {
            const profileRule = controller.filterRules.find(rule => rule.field === 'profileId');
            const firstProfile = profileRule?.textValues?.filter(Boolean)[0];
            if (firstProfile) profileIdToUse = firstProfile as string;
        }
        const folio = await FolioService.create({profileId: profileIdToUse, reservationId: props.reservationId});
        navigate(`/system/folios/${folio.id}`);
    };

    const content = (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: embedded ? '70vh' : '100%',
            minHeight: 0
        }}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        {!embedded && <ServerTableSearch/>}
                        <ServerTableSort/>
                        <ServerTableFilter options={filterOptions} disabled={!filterOptions.length}/>
                        <ServerTableFullscreen/>

                        <ServerTableColumns/>
                        <ServerTablePrint title={t('Folios')}/>
                        <ServerTableExport list="folios" title={t('Folios')} buildRequest={exportRequest}/>

                        <ServerTableCreate
                            label={t('Create folio')}
                            onClick={handleCreate}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </Box>
    );

    return embedded
        ? content
        : <SettingsListPage title={t('Folios')} back={() => navigate('/system')}>{content}</SettingsListPage>;
};

export default FoliosList;
